/**
 * Moisture readings (F4.4, F4-B2.b/c). Readings are integer TENTHS of a percent on the phone
 * (12.4 % → 124) so nothing is a float; the wire carries "12.4".
 */
import { divRoundHalfUp } from '@/lib/money'

export interface MoistureReadingLike {
  tenths: number
  chosen_by_farmer?: boolean
}

/** "12.4" → 124; null when not a percent with at most one decimal. */
export function parsePctTenths(input: string): number | null {
  const m = /^(\d{1,3})(?:[.,](\d?))?$/.exec(input.trim())
  if (!m) return null
  return Number(m[1]) * 10 + Number(m[2] || '0')
}

export function tenthsToString(tenths: number): string {
  return `${Math.trunc(tenths / 10)}.${tenths % 10}`
}

export function readingInRange(tenths: number, minTenths = 30, maxTenths = 400): boolean {
  return tenths >= minTenths && tenths <= maxTenths
}

/** |r1 − r2| > spread (1.5) → a third reading is required. */
export function needsThirdReading(
  r1: number | null,
  r2: number | null,
  spreadTenths = 15,
): boolean {
  if (r1 === null || r2 === null) return false
  return Math.abs(r1 - r2) > spreadTenths
}

/**
 * The lot's moisture figure: mean of two or median of three (F4.4), rounded half up to 0.1; once
 * the farmer has had a re-draw in a dispute, the mean of all readings (F4-B2.c). Null if none.
 */
export function moistureFigure(readings: readonly MoistureReadingLike[]): number | null {
  const v = readings.map((r) => r.tenths)
  if (v.length === 0) return null
  const redraw = readings.some((r) => r.chosen_by_farmer)
  if (v.length === 3 && !redraw) {
    return [...v].sort((a, b) => a - b)[1]!
  }
  const sum = v.reduce((a, b) => a + b, 0)
  return Number(divRoundHalfUp(BigInt(sum), BigInt(v.length)))
}

/**
 * Two different good bags for the moisture draws, picked by the phone (F4.3), 1-based.
 * One good bag → that bag twice. `random` is injectable for tests.
 */
export function pickMoistureBags(goodBags: number, random: () => number = Math.random): number[] {
  if (goodBags <= 0) return []
  if (goodBags === 1) return [1, 1]
  const a = 1 + Math.floor(random() * goodBags)
  let b = 1 + Math.floor(random() * (goodBags - 1))
  if (b >= a) b += 1
  return [a, b].sort((x, y) => x - y)
}
